// src/components/MotoristaDetalhesModal.js

import React, { useState, useEffect, useCallback } from 'react'; 
import api from '../services/api';
import { format, formatDistanceToNow } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import './MotoristaDetalhesModal.css';
import { 
    FaTimes, FaDollarSign, FaChartBar, FaCheckCircle, 
    FaStar, FaMoneyBillWave, FaListAlt, FaCarAlt, FaSpinner, 
    FaTrophy, FaEye, FaWrench, FaExclamationCircle
} from 'react-icons/fa'; 

const formatCurrency = (value) => (parseFloat(value || 0)).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export default function MotoristaDetalhesModal({ driverId, onClose }) {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userProfile, setUserProfile] = useState(null); 

    // Controle do novo Modal de Histórico
    const [isHistoricoOpen, setIsHistoricoOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('servicos'); // 'servicos' | 'pagamentos'

    const fetchUserProfile = useCallback(async () => {
        try {
            const { data } = await api.get('/api/usuarios/me');
            setUserProfile(data.perfil);
        } catch (error) {
            console.error("Erro ao buscar perfil:", error);
        }
    }, []);

    const fetchDetails = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await api.get(`/api/drivers/${driverId}/details`);
            setData(response.data);
        } catch (err) {
            setError('Não foi possível carregar os detalhes de performance.');
        } finally {
            setIsLoading(false);
        }
    }, [driverId]);

    useEffect(() => {
        if (!driverId) return;
        fetchUserProfile(); 
        fetchDetails(); 
    }, [driverId, fetchDetails, fetchUserProfile]);

    // --- COMPONENTE DO NOVO MODAL DE HISTÓRICO ---
    const ModalHistorico = () => {
        if (!isHistoricoOpen || !data) return null;
        const { history } = data;

        const renderDate = (dateString) => {
            if (!dateString) return 'N/D';
            try { return format(new Date(dateString), 'dd MMM yyyy, HH:mm', { locale: ptBR }); } 
            catch { return 'Inválido'; }
        };

        return (
            <div className="mot-dm-historico-overlay" onClick={() => setIsHistoricoOpen(false)}>
                <div className="mot-dm-historico-modal" onClick={e => e.stopPropagation()}>
                    <div className="mot-dm-hm-header">
                        <h2>Histórico do Motorista</h2>
                        <button onClick={() => setIsHistoricoOpen(false)} className="mot-dm-btn-close"><FaTimes /></button>
                    </div>
                    
                    <div className="mot-dm-history-tabs">
                        <button 
                            className={`mot-dm-h-tab ${activeTab === 'servicos' ? 'active' : ''}`}
                            onClick={() => setActiveTab('servicos')}
                        >
                            <FaWrench /> Ordens de Serviço Realizadas
                        </button>
                        <button 
                            className={`mot-dm-h-tab ${activeTab === 'pagamentos' ? 'active' : ''}`}
                            onClick={() => setActiveTab('pagamentos')}
                        >
                            <FaMoneyBillWave /> Repasses e Pagamentos
                        </button>
                    </div>

                    <div className="mot-dm-hm-body">
                        {activeTab === 'servicos' && (
                            <div className="mot-dm-timeline animate-fade-in">
                                {history.ordensDeServico.length > 0 ? history.ordensDeServico.map(os => (
                                    <div key={`os-${os.id}`} className="mot-dm-tl-item">
                                        <div className="mot-dm-tl-dot blue"></div>
                                        <div className="mot-dm-tl-card">
                                            <div className="mot-dm-tl-header">
                                                <span className="mot-dm-tl-id">OS #{os.id}</span>
                                                <span className="mot-dm-tl-date">{renderDate(os.data_referencia)}</span>
                                            </div>
                                            <p className="mot-dm-tl-desc">{os.descricao || 'Serviço sem descrição detalhada.'}</p>
                                            <div className="mot-dm-tl-footer">
                                                <span className={`mot-dm-os-status status-${os.status?.toLowerCase().replace(/\s+/g, '-')}`}>
                                                    {os.status}
                                                </span>
                                                <span className="mot-dm-tl-value">{formatCurrency(os.valor)}</span>
                                            </div>
                                        </div>
                                    </div>
                                )) : (
                                    <div className="mot-dm-empty-timeline">Nenhuma Ordem de Serviço registrada no histórico.</div>
                                )}
                            </div>
                        )}

                        {activeTab === 'pagamentos' && (
                            <div className="mot-dm-timeline animate-fade-in">
                                {history.pagamentos.length > 0 ? history.pagamentos.map(pg => (
                                    <div key={`pg-${pg.id}`} className="mot-dm-tl-item">
                                        <div className="mot-dm-tl-dot orange"></div>
                                        <div className="mot-dm-tl-card">
                                            <div className="mot-dm-tl-header">
                                                <span className="mot-dm-tl-id text-orange"><FaMoneyBillWave /> Repasse Realizado</span>
                                                <span className="mot-dm-tl-date">{renderDate(pg.data)}</span>
                                            </div>
                                            <p className="mot-dm-tl-desc">{pg.descricao || 'Pagamento registrado no sistema.'}</p>
                                            <div className="mot-dm-tl-footer">
                                                <span className="mot-dm-os-status status-concluido">Pago</span>
                                                <span className="mot-dm-tl-value text-orange">{formatCurrency(pg.valor)}</span>
                                            </div>
                                        </div>
                                    </div>
                                )) : (
                                    <div className="mot-dm-empty-timeline">Nenhum repasse financeiro registrado.</div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderContent = () => {
        if (isLoading && !data) return (
            <div className="mot-dm-state-box">
                <FaSpinner className="mot-dm-icon-spin" />
                <p>Processando analytics do motorista...</p>
            </div>
        );
        
        if (error) return (
            <div className="mot-dm-state-box error">
                <FaExclamationCircle className="mot-dm-icon-error" />
                <p>{error}</p>
            </div>
        );
        
        if (!data) return null;

        const { details, stats } = data;
        const faturamentoGerado = parseFloat(stats.faturamento_gerado || 0);
        const valorTotalPago = parseFloat(stats.valor_total_pago || 0);
        const balancoFinanceiro = faturamentoGerado - valorTotalPago;

        const eficiencia = stats.os_concluidas_total > 0 ? (faturamentoGerado / stats.os_concluidas_total) : 0;
        const margemLucro = faturamentoGerado - valorTotalPago;
        const retornoFinanceiro = faturamentoGerado > 0 ? (margemLucro / faturamentoGerado) * 100 : 0;
        
        const isAdmin = userProfile === 'admin_geral' || userProfile === 'admin';
        const isLowPerformance = retornoFinanceiro < 90;
        const performanceClass = isAdmin && isLowPerformance ? 'low-performance-alert' : '';
        
        const balancoColor = balancoFinanceiro >= 0 ? "text-green" : "text-red";
        const mostUsedVehicle = stats.most_used_vehicle; 
        
        const renderShortDate = (dateString) => {
            if (!dateString) return 'N/D';
            try { return format(new Date(dateString), 'dd/MM/yy', { locale: ptBR }); } 
            catch { return 'Inválido'; }
        };

        return (
            <>
                <header className="mot-dm-header">
                    <div className="mot-dm-profile">
                        <div className="mot-dm-avatar">{details.nome.charAt(0).toUpperCase()}</div>
                        <div>
                            <h2>{details.nome}</h2>
                            <span className="mot-dm-doc">CNH: {details.cnh_numero || 'Não informada'} (Cat. {details.categoria_cnh || '-'})</span>
                        </div>
                    </div>
                    <button onClick={onClose} className="mot-dm-close" title="Fechar"><FaTimes /></button>
                </header>
                
                <div className="mot-dm-body">
                    
                    {/* LINHA 1: INSIGHTS FINANCEIROS */}
                    <div className="mot-dm-grid-4">
                        <div className="mot-dm-card">
                            <div className="mot-dm-icon blue"><FaCheckCircle /></div>
                            <div className="mot-dm-info">
                                <label>OS Concluídas</label>
                                <strong>{stats.os_concluidas_total || 0}</strong>
                            </div>
                        </div>
                        <div className="mot-dm-card">
                            <div className="mot-dm-icon green"><FaDollarSign /></div>
                            <div className="mot-dm-info">
                                <label>Faturamento Gerado</label>
                                <strong>{formatCurrency(faturamentoGerado)}</strong>
                            </div>
                        </div>
                        <div className="mot-dm-card">
                            <div className="mot-dm-icon orange"><FaMoneyBillWave /></div>
                            <div className="mot-dm-info">
                                <label>Valor Repassado</label>
                                <strong>{formatCurrency(valorTotalPago)}</strong>
                            </div>
                        </div>
                        <div className="mot-dm-card">
                            <div className={`mot-dm-icon ${balancoFinanceiro >= 0 ? 'green' : 'red'}`}><FaChartBar /></div>
                            <div className="mot-dm-info">
                                <label>Balanço da Empresa</label>
                                <strong className={balancoColor}>{formatCurrency(balancoFinanceiro)}</strong>
                            </div>
                        </div>
                    </div>

                    {/* LINHA 2: PERFORMANCE E ENGAJAMENTO */}
                    <h3 className="mot-dm-section-title"><FaStar/> Painel de Performance</h3>
                    <div className="mot-dm-grid-3">
                        <div className="mot-dm-card-highlight">
                            <label>1. Eficiência Média</label>
                            <strong>{formatCurrency(eficiencia)}</strong>
                            <span>Ticket por serviço concluído</span>
                        </div>
                        
                        <div className={`mot-dm-card-highlight ${performanceClass}`}>
                            <label>2. Retorno Financeiro</label>
                            <strong>{retornoFinanceiro.toFixed(1)}%</strong>
                            <span>Margem de Lucro Bruto</span>
                        </div>

                        <div className="mot-dm-card-highlight">
                            <label>3. Ranking da Frota</label>
                            <strong><FaTrophy className="text-yellow" /> {stats.ranking_pos ? `#${stats.ranking_pos} de ${stats.total_motoristas}` : 'N/D'}</strong>
                            <span>Baseado no faturamento</span>
                        </div>

                        <div className="mot-dm-card-highlight">
                            <label>4. Última Atividade</label>
                            <strong>{stats.ultima_atividade ? formatDistanceToNow(new Date(stats.ultima_atividade), { locale: ptBR, addSuffix: true }) : 'Inativo'}</strong>
                            <span>{stats.ultima_atividade ? `Em: ${renderShortDate(stats.ultima_atividade)}` : 'Nenhuma OS concluída'}</span>
                        </div>

                        <div className="mot-dm-card-highlight span-2">
                            <label>5. Veículo Mais Utilizado</label>
                            {mostUsedVehicle && mostUsedVehicle.modelo ? (
                                <>
                                    <strong className="flex-center"><FaCarAlt /> {mostUsedVehicle.modelo}</strong>
                                    <span>Placa: {mostUsedVehicle.placa || 'N/D'} ({mostUsedVehicle.total_os || 0} OS)</span>
                                </>
                            ) : (
                                <>
                                    <strong>Nenhum Veículo</strong>
                                    <span>Baseado em OS concluídas</span>
                                </>
                            )}
                        </div>
                    </div>

                    {/* BOTÃO PARA ABRIR O NOVO MODAL DE HISTÓRICO */}
                    <div className="mot-dm-history-trigger">
                        <button onClick={() => setIsHistoricoOpen(true)} className="mot-dm-btn-history">
                            <FaEye /> Visualizar Histórico Completo da Linha do Tempo
                        </button>
                    </div>

                </div>
            </>
        );
    };

    return (
        <div className="mot-dm-overlay" onClick={onClose}>
            <div className="mot-dm-modal" onClick={e => e.stopPropagation()}>
                {renderContent()}
            </div>
            {/* Renderiza o modal secundário por cima */}
            <ModalHistorico />
        </div>
    );
}